import React, { useState, useEffect } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import { AdminUser, useUpdateUserProfile, useGetAllUsers } from '@SERVICES';

import useGlobalPermissions from '@HOOKS/useGlobalPermissions';
import useAuth from '@HOOKS/useAuth';

import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import COLOR from '@CONSTANTS/COLOR.constant';

import styles from './UserInfo.module.scss';

const cx: CX = classnames.bind(styles);

function UserInfo(props: Props) {
    const { user } = props;

    const {
        canManagePermissions,
    } = useGlobalPermissions();

    const [
        updateProfile,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = useUpdateUserProfile();

    const { state: { user: currentUser } } = useAuth();
    const { data: usersData = { data: [] } } = useGetAllUsers();

    // Get current user's full data including isSuperAdmin and canAccessAppSettings
    const currentUserFullData = usersData.data.find((u) => u.id === currentUser?.id);

    // Determine if current user can edit the target user's profile
    const canEditUserProfile = (() => {
        // If current user is not an admin, they cannot edit any profiles
        if (!currentUserFullData?.isSuperuser && !currentUserFullData?.isSuperAdmin) {
            return false;
        }

        // If current user is admin but not super admin, they cannot edit super admin profiles
        if (currentUserFullData?.isSuperuser && !currentUserFullData?.isSuperAdmin) {
            if (user.isSuperAdmin) {
                return false;
            }
        }

        // Super admins can edit anyone's profile
        if (currentUserFullData?.isSuperAdmin) {
            return true;
        }

        // Admins can edit regular users and other admins (but not super admins)
        if (currentUserFullData?.isSuperuser) {
            return !user.isSuperAdmin;
        }

        return false;
    })();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [formData, setFormData] = useState({
        fullName: user.fullName,
        email: user.email,
        country: user.country,
        mobilePhone: user.mobilePhone,
        officePhone: user.officePhone,
        title: user.title,
        userType: user.userType,
    });

    // Reset form data when user prop changes
    useEffect(() => {
        setFormData({
            fullName: user.fullName,
            email: user.email,
            country: user.country,
            mobilePhone: user.mobilePhone,
            officePhone: user.officePhone,
            title: user.title,
            userType: user.userType,
        });
    }, [user]);

    useEffect(() => {
        if (isSuccess) {
            setIsEditing(false);
            notify.info('User profile has been updated', {
                toastId: 'update-user-profile',
            });
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'update-user-profile-permission-error',
                });
            } else {
                notify.error('Something went wrong!', {
                    toastId: 'update-user-profile-error',
                });
            }
        }
    }, [isError, error]);

    const handleInputChange = (field: string) => (value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        updateProfile({
            id: user.id,
            fullName: formData.fullName,
            email: formData.email,
            country: formData.country,
            mobilePhone: formData.mobilePhone,
            officePhone: formData.officePhone,
            title: formData.title,
            userType: formData.userType,
        });
    };

    const handleCancel = () => {
        setFormData({
            fullName: user.fullName,
            email: user.email,
            country: user.country,
            mobilePhone: user.mobilePhone,
            officePhone: user.officePhone,
            title: user.title,
            userType: user.userType,
        });
        setIsEditing(false);
    };

    const dataItems = [
        {
            title: 'Name',
            field: 'fullName',
            value: isEditing ? formData.fullName : user.fullName,
        },
        {
            title: 'Email',
            field: 'email',
            value: isEditing ? formData.email : user.email,
        },
        {
            title: 'Country',
            field: 'country',
            value: isEditing ? formData.country : user.country,
        },
        {
            title: 'Mobile phone',
            field: 'mobilePhone',
            value: isEditing ? formData.mobilePhone : user.mobilePhone,
        },
        {
            title: 'Office phone',
            field: 'officePhone',
            value: isEditing ? formData.officePhone : user.officePhone,
        },
        {
            title: 'Title',
            field: 'title',
            value: isEditing ? formData.title : user.title,
        },
        {
            title: 'User type',
            field: 'userType',
            value: isEditing ? formData.userType : user.userType,
        },
    ];

    return (
        <div className={cx('user-info')}>
            <div className={cx('user-info-header')}>
                <div className={cx('user-info-title')}>
                    User Info
                </div>
                
                {canManagePermissions && canEditUserProfile && !isEditing && (
                    <BasicButton
                        title="Edit"
                        variant="secondary"
                        size="small"
                        style={{
                            height: 28,
                            width: 60,
                            fontSize: 11,
                        }}
                        onClick={() => setIsEditing(true)}
                    />
                )}
            </div>

            <div className={cx('data')}>
                {dataItems.map((item) => {
                    const key = item.title;

                    return (
                        <div
                            key={key}
                            className={cx('data-item')}
                        >
                            <div className={cx('data-title')}>
                                {item.title}
                            </div>
                            <div className={cx('data-value')}>
                                {isEditing ? (
                                    <BasicInput
                                        value={item.value || ''}
                                        onChange={handleInputChange(item.field)}
                                        placeholder={`Enter ${item.title.toLowerCase()}`}
                                    />
                                ) : (
                                    item.value || '-'
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isEditing && (
                <div className={cx('edit-actions')}>
                    <BasicButton
                        title="Cancel"
                        style={{
                            height: 32,
                            width: 70,
                            fontSize: 12,
                            backgroundColor: COLOR['marriott-light-grey'],
                            marginRight: 10,
                        }}
                        onClick={handleCancel}
                        locked={isLoading}
                    />
                    <BasicButton
                        title="Save"
                        isProcessing={isLoading}
                        style={{
                            height: 32,
                            width: 70,
                            fontSize: 12,
                            backgroundColor: COLOR['app-green'],
                        }}
                        onClick={handleSave}
                        locked={isLoading}
                    />
                </div>
            )}
        </div>
    );
}

type Props = {
    user: AdminUser,
};

export default UserInfo;
